import { useCallback, useEffect, useRef, useState } from "react";
import { postApiJson } from "../services/api";
import { useShareTranslation } from "./useShareTranslation";
import { parseDirection, shareLanguages } from "../utils/direction";

export interface UseEditorPanelOptions {
  inputText: string;
  setInputText: (text: string) => void;
  setOutputText: (text: string) => void;
  outputText: string;
  handleTranslit: () => void;
  selectedDirection: string;
  readOnly?: boolean;
}

export function useEditorPanel({
  inputText,
  setInputText,
  setOutputText,
  outputText,
  handleTranslit,
  selectedDirection,
  readOnly = false,
}: UseEditorPanelOptions) {
  const {
    createShare,
    clearShareData,
    loading: shareLoading,
    shareData,
  } = useShareTranslation();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const historyRef = useRef<string[]>([]);
  const currentIndexRef = useRef(-1);
  const isUndoingRef = useRef(false);
  const MAX_CHARS = 5000;
  const inputLength = inputText.length;
  const hasInput = inputText.trim().length > 0;
  const isOverLimit = inputLength > MAX_CHARS;
  const [clearedText, setClearedText] = useState<string | null>(null);
  const canUndoClear = clearedText !== null;
  const inputScript = parseDirection(selectedDirection).from;
  const specialChars =
    inputScript === "cyrillic"
      ? ["Ў", "ў", "Қ", "қ", "Ғ", "ғ", "Ҳ", "ҳ"]
      : inputScript === "newLatin"
        ? ["Ö", "ö", "Ğ", "ğ", "Ş", "ş", "Ç", "ç"]
        : ["O‘", "o‘", "G‘", "g‘", "’"];
  const [showCopied, setShowCopied] = useState(false);
  const [isLikeSending, setIsLikeSending] = useState(false);
  const [quickRating, setQuickRating] = useState<"like" | "dislike" | null>(
    null
  );

  const updateSelectionState = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    setSelection({
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
    });
  }, []);

  useEffect(() => {
    if (isUndoingRef.current) {
      isUndoingRef.current = false;
      return;
    }

    const currentHistoryValue =
      historyRef.current[currentIndexRef.current] || "";
    if (inputText !== currentHistoryValue) {
      const newHistory = historyRef.current.slice(
        0,
        currentIndexRef.current + 1
      );
      newHistory.push(inputText);
      historyRef.current = newHistory;
      currentIndexRef.current = newHistory.length - 1;
    }
  }, [inputText]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!inputText.trim()) {
      setOutputText("");
      return;
    }

    timeoutRef.current = setTimeout(() => {
      handleTranslit();
    }, 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [inputText, handleTranslit, readOnly, setOutputText]);

  const handleSpecialCharClick = useCallback(
    (char: string) => {
      if (readOnly) return;
      const start = selection.start;
      const end = selection.end;
      const newValue = inputText.slice(0, start) + char + inputText.slice(end);
      setInputText(newValue);

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const newPosition = selection.start + char.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
        setSelection({ start: newPosition, end: newPosition });
      });
    },
    [selection, setInputText, readOnly]
  );

  const handlePasteInput = useCallback(async () => {
    if (readOnly) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    try {
      const text = await navigator.clipboard.readText();

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newText = inputText.slice(0, start) + text + inputText.slice(end);
      setInputText(newText);

      requestAnimationFrame(() => {
        const newPosition = start + text.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
        setSelection({ start: newPosition, end: newPosition });
      });
    } catch (error) {
      console.error("Clipboarddan o'qishda xato:", error);
    }
  }, [inputText, setInputText, readOnly]);

  const handleClearInput = useCallback(() => {
    if (!hasInput || readOnly) return;
    setClearedText(inputText);
    setInputText("");
  }, [hasInput, inputText, setInputText, readOnly]);

  const handleUndo = useCallback(() => {
    if (!canUndoClear || readOnly) return;
    if (clearedText !== null) {
      setInputText(clearedText);
      setClearedText(null);
    }
  }, [canUndoClear, clearedText, setInputText, readOnly]);

  const handleCopyOutput = useCallback(async () => {
    if (!outputText) return;
    try {
      await navigator.clipboard.writeText(outputText);
    } catch (err) {
      console.error("Nusxalashda xatolik:", err);
    }
  }, [outputText]);

  const handleQuickLike = useCallback(async () => {
    if (isLikeSending || !outputText.trim() || readOnly) {
      return;
    }

    setIsLikeSending(true);
    try {
      await postApiJson<{ message: string }>("/ratings", {
        choice: "yes",
        body: "",
        type: "",
        contact: "",
        content: inputText,
        result: outputText,
      });
      setQuickRating("like");
    } catch (error) {
      console.error("Like rating error:", error);
    } finally {
      setIsLikeSending(false);
    }
  }, [inputText, outputText, isLikeSending, readOnly]);

  const handleShare = useCallback(async () => {
    if (!inputText.trim() || !outputText.trim() || readOnly) {
      return;
    }

    const { from, to } = shareLanguages(selectedDirection);

    await createShare(inputText, outputText, from, to);
  }, [inputText, outputText, selectedDirection, createShare, readOnly]);

  return {
    textareaRef,
    hasInput,
    canUndoClear,
    inputLength,
    isOverLimit,
    maxChars: MAX_CHARS,
    specialChars,
    showCopied,
    setShowCopied,
    handleCopyOutput,
    handlePasteInput,
    handleClearInput,
    handleUndo,
    handleSpecialCharClick,
    updateSelectionState,
    handleShare,
    shareData,
    shareLoading,
    clearShareData,
    quickRating,
    setQuickRating,
    handleQuickLike,
    readOnly,
  };
}
